(() => {
  'use strict';
  if (customElements.get('sofiaga-artwork-upload')) return;
  const cartURL = () => `${window.Shopify.routes.root}cart`;
  const isUploadURL = (value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && url.pathname.includes('/uploads/');
    } catch (_) { return false; }
  };
  const findArtwork = (value, artworkId) => {
    if (!value || typeof value !== 'object') return false;
    if (value.properties?._sofiaga_artwork_id === artworkId && isUploadURL(value.properties.Artwork)) return true;
    return Object.values(value).some((child) => child && typeof child === 'object' && findArtwork(child, artworkId));
  };

  class ArtworkUpload extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('[data-artwork-input]');
      this.form = document.getElementById(this.dataset.formId);
      this.owner = this.form?.closest('block-buy-buttons');
      if (!this.owner) return;
      this.file = null;
      this.pending = false;
      this.selectionGeneration = 0;
      this.bundle = this.dataset.isBundle === 'true';
      this.marker = document.createElement('input');
      this.marker.type = 'hidden';
      this.marker.name = 'properties[_sofiaga_artwork_id]';
      this.marker.setAttribute('form', this.dataset.formId);
      this.marker.disabled = true;
      this.append(this.marker);
      this.input.addEventListener('change', () => {
        if (this.pending) return;
        this.invalidateValidation();
        const generation = this.selectionGeneration;
        const file = this.input.files?.[0];
        if (!file) { this.clear(false); return; }
        const cancelled = new Promise((resolve) => { this.cancelValidation = resolve; });
        const validation = this.choose(file, generation).catch(() => {
          if (this.selectionGeneration !== generation || this.input.files?.[0] !== file) return;
          this.clear(false);
          this.fail(this.text('invalid'));
          this.input.setAttribute('aria-invalid', 'true');
        });
        this.validation = Promise.race([validation, cancelled]);
      });
      this.querySelector('[data-artwork-replace]').addEventListener('click', () => this.input.click());
      this.querySelector('[data-artwork-remove]').addEventListener('click', () => this.clear());
      this.querySelector('[data-artwork-retry]').addEventListener('click', () => this.form.requestSubmit());
      this.owner.sofiagaArtwork = this;
      customElements.whenDefined('block-buy-buttons').then(() => {
        this.owner.addVariantToCart = (...args) => this.submit(() => Object.getPrototypeOf(this.owner).addVariantToCart.apply(this.owner, args));
      });
      const position = () => {
        if (!this.bundle) {
          const addOn = this.owner.querySelector('[id="rbr-container-element-addOn"]');
          if (addOn?.children.length && addOn.nextElementSibling !== this) addOn.after(this);
          return;
        }
        const widget = this.owner.querySelector('[id="rbr-container-element-true"]');
        const ready = widget?.querySelector('.rbr-sbap_body, .rbr-bap-new-body, [id^="rbr-sitem-"]');
        if (ready && widget.nextElementSibling !== this) widget.after(this);
        this.hidden = !ready;
      };
      this.observer = new MutationObserver(position);
      this.observer.observe(this.owner, { childList: true, subtree: true });
      position();
      this.form.addEventListener('submit', (event) => {
        if (this.owner.cartType !== 'page' || (!this.file && !this.input.files?.length && this.dataset.required !== 'true')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.pageSubmit();
      }, true);
    }

    text(key) {
      return this.dataset[`locale${key[0].toUpperCase()}${key.slice(1)}`] || this.dataset.localeError;
    }

    status(state, text) {
      this.dataset.state = state;
      this.querySelector('[data-artwork-status]').textContent = text || '';
      this.setAttribute('aria-busy', String(state === 'uploading' || state === 'verifying'));
    }

    fail(message, retry = false) {
      this.status('error', '');
      const error = this.querySelector('[data-artwork-error]');
      error.textContent = message;
      error.hidden = false;
      this.querySelector('[data-artwork-retry]').hidden = !retry;
    }

    resetError() {
      this.querySelector('[data-artwork-error]').hidden = true;
      this.querySelector('[data-artwork-retry]').hidden = true;
      this.input.removeAttribute('aria-invalid');
    }

    invalidateValidation() {
      this.selectionGeneration++;
      this.cancelValidation?.();
      this.cancelValidation = null;
      this.validation = null;
    }

    async choose(file, generation) {
      if (this.pending) return;
      if (!file) return;
      this.resetError();
      const extension = file.name.split('.').pop().toLowerCase();
      const bytes = new Uint8Array(await file.slice(0, 8).arrayBuffer());
      if (this.selectionGeneration !== generation || this.input.files?.[0] !== file) return;
      const png = extension === 'png' && bytes.slice(0, 8).join(',') === '137,80,78,71,13,10,26,10';
      const jpg = ['jpg', 'jpeg'].includes(extension) && bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
      const pdf = extension === 'pdf' && String.fromCharCode(...bytes.slice(0, 5)) === '%PDF-';
      if (!file.size || !(png || jpg || pdf) || file.size > Number(this.dataset.maxBytes)) {
        this.clear(false);
        this.fail(this.text(file.size > Number(this.dataset.maxBytes) ? 'tooLarge' : 'invalid'));
        this.input.setAttribute('aria-invalid', 'true');
        return;
      }
      this.releasePreview();
      this.file = file;
      this.marker.disabled = false;
      this.querySelector('[data-artwork-filename]').textContent = file.name;
      this.querySelector('[data-artwork-filesize]').textContent = file.size < 1048576
        ? `${Math.max(1, Math.round(file.size / 1024))} KB`
        : `${(file.size / 1048576).toLocaleString(document.documentElement.lang, { maximumFractionDigits: 1 })} MB`;
      const preview = this.querySelector('[data-artwork-preview]');
      if (png || jpg) {
        this.previewURL = URL.createObjectURL(file);
        preview.src = this.previewURL;
        preview.hidden = false;
      }
      this.querySelector('[data-artwork-file-icon]').hidden = png || jpg;
      this.querySelector('[data-artwork-picker]').hidden = true;
      this.querySelector('[data-artwork-selection]').hidden = false;
      this.status('selected', this.text('selected'));
      this.querySelector('[data-artwork-replace]').focus({ preventScroll: true });
    }

    releasePreview() {
      if (this.previewURL) URL.revokeObjectURL(this.previewURL);
      this.previewURL = null;
      const preview = this.querySelector('[data-artwork-preview]');
      preview.removeAttribute('src');
      preview.hidden = true;
    }

    clear(focus = true) {
      if (this.pending) return;
      this.invalidateValidation();
      this.file = null;
      this.input.value = '';
      this.marker.disabled = true;
      this.releasePreview();
      this.resetError();
      this.querySelector('[data-artwork-picker]').hidden = false;
      this.querySelector('[data-artwork-selection]').hidden = true;
      this.status('empty', '');
      if (focus) this.input.focus({ preventScroll: true });
    }

    async pageSubmit() {
      if (this.pending) return;
      this.owner.disableAddToCartButton();
      try {
        await this.owner.addVariantToCart();
        window.location.assign(cartURL());
      } catch (error) {
        if (!error.sofiagaArtwork) this.owner.handleError(error);
      } finally { this.owner.enableAddToCartButton(); }
    }

    async submit(add) {
      while (this.validation) {
        const validation = this.validation;
        await validation;
        if (this.validation === validation) break;
      }
      if (this.pending) throw Object.assign(new Error(this.text('busy')), { description: this.text('busy'), sofiagaArtwork: true });
      if (!this.file) {
        if (this.dataset.required === 'true') {
          this.fail(this.text('required'));
          this.input.focus();
          throw Object.assign(new Error(this.text('required')), { description: this.text('required'), sofiagaArtwork: true });
        }
        this.marker.disabled = true;
        return add();
      }
      this.pending = true;
      this.resetError();
      this.querySelectorAll('button').forEach((button) => { button.disabled = true; });
      const artworkId = crypto.randomUUID();
      this.marker.value = artworkId;
      this.status('uploading', this.text('uploading'));
      let session;
      let requestStarted = false;
      let addResolved = false;
      try {
        if (this.bundle) {
          if (!window.SofiagaArtworkBridge) throw new Error('bridge_unavailable');
          session = window.SofiagaArtworkBridge.begin({ productId: this.dataset.productId, file: this.file, artworkId });
        }
        requestStarted = true;
        const result = await add();
        addResolved = true;
        this.status('verifying', this.text('verifying'));
        const read = window.SofiagaArtworkBridge?.originalFetch || window.fetch.bind(window);
        const verificationURL = session ? `${cartURL()}?section_id=sofiaga-artwork-status` : `${window.Shopify.routes.root}cart.js`;
        const response = await read(verificationURL, { cache: 'no-store' });
        if (!response.ok) throw new Error('cart_read_failed');
        let confirmed;
        if (session) {
          const rendered = new DOMParser().parseFromString(await response.text(), 'text/html');
          confirmed = session.uploadSent && session.groupId && Array.from(rendered.querySelectorAll('[data-sofiaga-artwork-record]')).some((record) =>
            record.dataset.artworkId === artworkId && record.dataset.bundleId === String(session.groupId) && isUploadURL(record.dataset.artworkUrl));
        } else {
          const cart = await response.json();
          confirmed = findArtwork(cart.items, artworkId);
        }
        if (!confirmed) throw new Error('upload_not_confirmed');
        this.status('saved', this.text('saved'));
        return result;
      } catch (error) {
        const uncertain = addResolved || requestStarted;
        const unsupported = ['bundle_not_ready', 'unsupported_bundle', 'bridge_unavailable'].includes(error.code || error.message);
        const message = uncertain ? this.text('unverified') : unsupported ? this.text('unavailable') : this.text('error');
        this.fail(message, !uncertain && !unsupported);
        throw Object.assign(error, { description: message, sofiagaArtwork: true });
      } finally {
        if (session) window.SofiagaArtworkBridge.end(session);
        this.pending = false;
        this.querySelectorAll('button').forEach((button) => { button.disabled = false; });
      }
    }
  }
  customElements.define('sofiaga-artwork-upload', ArtworkUpload);
})();
